import React, { FC, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import makeStyles from '@mui/styles/makeStyles';
import Grid from '@mui/material/Grid';

import { NewsFeed } from 'modules/common';
import { RenderElement } from 'shared/hoc';
import { useFeatures, useQuery } from 'shared/hooks';
import { BaseProps } from 'shared/types/base';
import { allStandardRolesTypes } from 'shared/constants/rolesTypes';
import { downloadService } from 'services/download';
import { addNotificationAction } from 'store/notifications/notificationsActions';
import { NotificationsWidget } from 'modules/notificationsCentre/components/NotificationsWidget';

import { HomeESGCoverage } from './HomeESGCoverage/HomeESGCoverage';
import { HomeRatingCoverage } from './HomeRatingCoverage/HomeRatingCoverage';
import { HomeTrendingRatings } from './HomeTrendingRatings/HomeTrendingRatings';
import { HomeRecentRatings } from './HomeRecentRatings/HomeRecentRatings';
import { HomeRecentPortfolios } from './HomeRecentPortfolios/HomeRecentPortfolios';
import { HomeContactBox } from './HomeContactBox/HomeContactBox';
import { HomeMyOrganisation } from './HomeMyOrganisation/HomeMyOrganisation';
import { HomeSubscriptions } from './HomeSubscriptions/HomeSubscriptions';
import { CoveredBonds } from './HomeCoveredBonds/HomeCoveredBonds';

type HomeProps = BaseProps;

type HandleDownloadParams = {
  path: string;
  name: string;
};

const useStyle = makeStyles(theme => ({
  panel: {
    marginBottom: theme.spacing(3),
  },
}));

const Home: FC<HomeProps> = () => {
  const dispatch = useDispatch();
  const classes = useStyle();
  const { document } = useQuery();
  const { ecafFeature } = useFeatures();

  const throwFetchError = () =>
    dispatch(addNotificationAction({ variant: 'error', message: 'Failed to fetch document.' }));

  const handleDownload = ({ path, name }: HandleDownloadParams) =>
    downloadService({ path, name, onError: throwFetchError });

  useEffect(() => {
    if (document === 'methodology') {
      handleDownload({
        name: 'ESG Impact Review Methodology.pdf',
        path: '/esg/api/methodology-current/',
      });
    }
    if (document === 'how-we-are-different') {
      handleDownload({
        name: 'Scope ESG Impact Review - How we are different.pdf',
        path: '/esg/api/how-we-are-different-current/',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Grid container spacing={3}>
      <RenderElement role={allStandardRolesTypes}>
        <Grid item xs={12} md={3}>
          <div className={classes.panel}>
            <HomeESGCoverage />
          </div>
          <div className={classes.panel}>
            <HomeRatingCoverage />
          </div>
          {ecafFeature && (
            <div className={classes.panel}>
              <CoveredBonds />
            </div>
          )}
          <div className={classes.panel}>
            <HomeTrendingRatings />
          </div>
          <div className={classes.panel}>
            <HomeRecentRatings />
          </div>
        </Grid>
      </RenderElement>
      <Grid item xs={12} md={6}>
        <NewsFeed />
      </Grid>
      <Grid item xs={12} md={3}>
        <div className={classes.panel}>
          <HomeContactBox />
        </div>
        {false && (
          <div className={classes.panel}>
            <NotificationsWidget />
          </div>
        )}
        <div className={classes.panel}>
          <HomeMyOrganisation />
        </div>
        <div className={classes.panel}>
          <HomeRecentPortfolios />
        </div>
        <HomeSubscriptions />
      </Grid>
    </Grid>
  );
};

export { Home };
